import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../../store';
import { IntakeTabValidationKey } from '../../store/slices/intakeSlice';
import { resolveErrorFieldAction } from './intakeErrorFieldRouting';
import { AddressSuggestionModal } from '../../pages/CreatePatientForm/AddressSuggestionModal';
import { initializeEmrSelectionEndpointIntakeIntakeIdInitializeEmrSelectionPost } from '../../services/api/sdk.gen';
import {
  Alert,
  AlertDescription,
  Card,
  CardHeader,
  CardContent,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import {
  AlertTriangle,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  CalendarDays,
  ExternalLink,
} from 'lucide-react';
import { useCreateEpisodeStepForm } from '../../hooks/useCreatePatientForm/useCreateEpisodeStepForm';
import { useKinnserScheduling } from '../../hooks/useCreatePatientForm/useKinnserScheduling';
import { useKinnserAutoAddFlow } from '../../hooks/useCreatePatientForm/useKinnserAutoAddFlow';
import { useRogerScheduling } from '../../hooks/useCreatePatientForm/useRogerScheduling';
import {
  computeAndSetIntakeTabValidation,
  validateSchedulingFromIntakeWithClinicData,
} from '../../hooks/useIntakeTabValidationOnLoad';
import { useSchedulingClinicData } from '../../hooks/useCreatePatientForm/useSchedulingClinicData';
import { useClinicSourceSchedulingEmr } from '../../hooks/useCreatePatientForm/useClinicSourceSchedulingEmr';
import { useIntakeFormSchema } from '../../config/intakeFormSchema';
import { useIntake } from '../../hooks/useIntake';
import { useToast } from '../../hooks/useToastContext';
import { isClinicWrmEnabled } from '../../utils/clinicKeyUtils';
import { derivePushMode } from './pushMode';
import { isSchedulingPushCompleted } from './schedulingVisitType';
import { getRecDerivedDates } from '../../utils/recSchedulingDates';
import { RecOnboardingSummaryLines } from './RecOnboardingSummaryLines';
import { PatientCreateButton } from './PatientCreateButton';
import MarkCompletedButton from './MarkCompletedButton';
import { KinnserCreateEpisodeStepFormContent } from '../../pages/CreatePatientForm/KinnserCreateEpisodeStepFormContent';
import { RogerCreateEpisodeStepFormContent } from '../../pages/CreatePatientForm/RogerCreateEpisodeStepFormContent';
import type {
  KinnserCreatePatientResponse,
  RogerOnboardPatientResponse,
} from '../../services/api/types.gen';
import type {
  KinnserOnboardPatientResponse,
  KinnserOnboardValidationError,
} from '../../hooks/useCreatePatientForm/useKinnserOnboarding';
import type { RogerOnboardResultWithSurfaceable } from '../../hooks/useCreatePatientForm/useRogerCreation';
import { getFieldLabel } from '../../utils/kinnserFieldMapping';
import { IntakeValidationErrorList } from './IntakeValidationErrorList';
import type { IntakeValidationErrorItem } from './intakeValidationErrorItems';
import { SchedulingEmrSelectionHeader } from './SchedulingEmrSelectionHeader';
import { FeatureFlag } from '../../constants/featureFlags';
import type { CreateEpisodeStepFormData } from '../../schemas/createEpisodeStepFormSchema';

/** Maps intake tab validation keys to Intake3 tab ids (see INTAKE_TAB_NAMES). */
const VALIDATION_KEY_TO_INTAKE_TAB: Record<IntakeTabValidationKey, string> = {
  [IntakeTabValidationKey.Extraction]: 'extraction',
  [IntakeTabValidationKey.Eligibility]: 'eligibility',
  [IntakeTabValidationKey.PriorAuth]: 'prior-auth',
  [IntakeTabValidationKey.ReadyToCall]: 'ready-to-call',
  [IntakeTabValidationKey.Scheduling]: 'scheduling',
};

interface CreateEpisodeStepProps {
  onComplete?: () => Promise<void>;
  onStatusChange?: () => void | Promise<void>;
  /** Scroll to a field on this tab when the user clicks a validation link. */
  onScrollToField?: (fieldName: string) => void;
  /** Navigate to another intake tab when the user clicks an incomplete step link. */
  onNavigateToTab?: (tabId: string) => void;
  /** Upload name -> display name for intake documents */
  documentNameDict?: Record<string, string>;
}

type DisplayCreateResult =
  | KinnserCreatePatientResponse
  | KinnserOnboardPatientResponse
  | RogerOnboardPatientResponse
  | null;

const getStringField = (value: unknown, fieldName: string): string | null => {
  if (value == null || typeof value !== 'object' || !(fieldName in value)) {
    return null;
  }

  const fieldValue = (value as Record<string, unknown>)[fieldName];
  return typeof fieldValue === 'string' && fieldValue.trim() !== ''
    ? fieldValue
    : null;
};

const CreateEpisodeStep: React.FC<CreateEpisodeStepProps> = ({
  onComplete,
  onStatusChange,
  onScrollToField,
  onNavigateToTab,
  documentNameDict,
}) => {
  const user = useStore(state => state.user);
  const agencyClinics = useStore(state => state.agencyClinics);
  const hasFeatureFlag = useStore(state => state.hasFeatureFlag);
  const { confirmTabStatus } = useIntake();
  const { error: showError } = useToast();
  const intakeTabValidation = useStore(state => state.intakeTabValidation);
  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);
  const { schema: intakeFormSchema } = useIntakeFormSchema();
  const hasKinnserOption = user?.agency?.is_kinnser_enabled === true;
  const showAIExplanations = hasFeatureFlag(
    FeatureFlag.INTAKE_SEE_AI_EXPLANATION
  );

  const {
    // Form
    control,
    register,
    errors,
    setValue,
    getValues,
    handleFieldBlur,

    // Watched values
    hhCahpsOptOut,
    kinnserLanguageKey,
    pushToRoger,
    pushToKinnser,

    // Save status
    saveStatus,
    saveToServer,

    // Clinics
    clinics,
    clinicData,
    loadingClinics,
    loadingClinicData,
    insuranceOptions,

    // Roger physician options
    rogerPhysicianOptions,
    loadingPhysicians,

    // Form validation
    isValid,

    // Create Episode Generic
    createResult,
    creatingInEMR,
    isUsingOnboarding,

    // Kinnser creation/onboarding
    handleCreateInKinnser,

    // Roger creation
    handleCreateInRoger,
    rogerCreateResult,

    // Intake data
    selectedIntake,

    // Address validation
    addressSuggestion,
    addressModalOpen,
    isValidatingAddress,
    setAddressModalOpen,
    handleAddressFieldBlur,
    handleAcceptAddressSuggestion,

    // Clinic field handler
    handleClinicFieldBlur,
  } = useCreateEpisodeStepForm();

  // ROC / SOC-existing modes. `kinnser_visit_type` is a SHARED key used by
  // both Kinnser and Roger scheduling (historical name).
  const visitType = selectedIntake?.kinnser_visit_type;

  // Raw new/existing decision: true = new, false = existing, null = undecided.
  const isNewPatientRaw = selectedIntake?.is_new_patient ?? null;
  const isExistingPatient = isNewPatientRaw === false;
  const isNewPatient = isNewPatientRaw === true;
  const patientMatchSource =
    (
      selectedIntake as {
        patient_match_source?: 'roger_emr' | 'kinnser' | null;
      }
    )?.patient_match_source ?? null;
  const scheduleKinnserBlockedByMatch =
    isExistingPatient && patientMatchSource === 'roger_emr';
  const scheduleRogerBlockedByMatch =
    isExistingPatient && patientMatchSource === 'kinnser';

  const {
    schedulingEmr,
    showRogerSchedulingOption,
    showKinnserSchedulingOption,
    schedulingSelectionLocked,
  } = useClinicSourceSchedulingEmr({
    agencyClinics,
    agencyClinicId: selectedIntake?.agency_clinic_id,
    // Pass raw is_new_patient (may be null) so create-episode flags match
    // SchedulingStep / backend (only true → kinnser_create_episode).
    isNewPatient: isNewPatientRaw,
    intakeSchedulingFlags: {
      roger_create_episode: selectedIntake?.roger_create_episode,
      kinnser_create_episode: selectedIntake?.kinnser_create_episode,
      push_to_roger: selectedIntake?.push_to_roger,
      push_to_kinnser: selectedIntake?.push_to_kinnser,
    },
    rogerAgencyEnabled: user?.agency?.is_rogeremr_enabled === true,
    kinnserAgencyEnabled: hasKinnserOption,
    scheduleRogerBlockedByMatch,
    scheduleKinnserBlockedByMatch,
    getValues,
    setValue,
    saveToServer,
  });

  // SOC new admission + episode or new episode: auto_add (creates SOC) -> doc upload -> task details.
  const creationType = (
    selectedIntake as { kinnser_creation_type?: string | null }
  )?.kinnser_creation_type as
    | 'new_admission'
    | 'new_episode'
    | null
    | undefined;

  // Canonical push-mode derivation — see pushMode.ts for the taxonomy and the
  // invariants its tests enforce (an existing patient never derives `create`).
  const pushMode = derivePushMode({
    isNewPatient: isNewPatientRaw,
    visitType: visitType ?? null,
    creationType: creationType ?? null,
    pushToKinnser: pushToKinnser === true,
    pushToRoger: pushToRoger === true,
  });

  const isRocMode = pushMode.kind === 'roc';
  const isRocModeRoger = isRocMode && pushToRoger === true;
  const isSocExistingPatientMode = pushMode.kind === 'soc_existing';
  const isSocExistingPatientModeRoger =
    isSocExistingPatientMode && pushToRoger === true;
  // Kinnser SOC-existing via auto_add (new admission / new episode). New and
  // undecided patients derive `create` instead — they always onboard.
  const isSocNewAdmissionOrEpisodeModeForFlow =
    pushMode.kind === 'soc_existing' && pushMode.kinnserStrategy === 'auto_add';
  // Existing patients push via scheduling flows (ROC / SOC-existing / auto-add), all
  // of which need a visit type picked on the Scheduling tab. Until then, gate the
  // push on Scheduling — NOT on the new-patient create-form fields, which don't
  // apply to existing patients (and whose handler would create a duplicate patient).
  const awaitingSchedulingSelection = pushMode.kind === 'awaiting_scheduling';

  // Matched patient has an open admission (active, no discharge) → Kinnser cannot
  // create a new SOC until it is discharged/inactivated. Mirrors the Scheduling tab's
  // `socBlockedByOpenAdmission` so a SOC that is blocked there cannot be pushed here.
  const socBlockedByOpenAdmission =
    isExistingPatient &&
    Boolean(
      (
        selectedIntake as {
          kinnser_matched_has_open_admission?: boolean | null;
        }
      )?.kinnser_matched_has_open_admission
    );
  const socKinnserBlockedByOpenAdmission = Boolean(
    pushToKinnser && visitType === 'soc' && socBlockedByOpenAdmission
  );

  const shouldCreateEpisode = !!(
    selectedIntake?.kinnser_create_episode ||
    selectedIntake?.roger_create_episode
  );
  // Label-only (Onboard button copy / Mark Complete label): SOC with new
  // admission/episode regardless of the new/existing decision. Flow selection
  // uses `isSocNewAdmissionOrEpisodeModeForFlow` derived from pushMode above.
  const isSocNewAdmissionOrEpisodeMode =
    pushToKinnser &&
    visitType === 'soc' &&
    (creationType === 'new_admission' || creationType === 'new_episode');

  const kinnserTypeOfSoc = (
    selectedIntake as { kinnser_type_of_soc?: string | null }
  )?.kinnser_type_of_soc;
  const schedulingTabName: 'nursing' | 'pt' =
    kinnserTypeOfSoc === 'PT' ? 'pt' : 'nursing';

  // Unified flow for ROC and SOC existing: create task on existing episode, upload docs, update task details.
  const schedulingTaskName = isSocExistingPatientMode
    ? kinnserTypeOfSoc === 'PT'
      ? 'OASIS-E2 Start of Care (PT)'
      : 'OASIS-E2 Start of Care'
    : 'OASIS-E2 Resumption of Care';
  const rocDate = (selectedIntake as { roc_date?: string | null })?.roc_date;
  const socDateRaw = (selectedIntake as { kinnser_soc_date?: string | null })
    ?.kinnser_soc_date;
  const socDateOnly =
    socDateRaw && typeof socDateRaw === 'string'
      ? socDateRaw.split('T')[0]
      : null;
  const schedulingTaskDate = isSocExistingPatientMode
    ? socDateOnly
    : (rocDate ?? null);
  const selectedKinnserClinicianId =
    selectedIntake?.kinnser_clinician_id &&
    selectedIntake.kinnser_clinician_id.trim() !== ''
      ? selectedIntake.kinnser_clinician_id.trim()
      : null;

  // Kinnser-specific match identity. Roger matches are represented by
  // `selectedIntake.patient_id` (see `hasMatchedRogerPatient` below); do not
  // conflate the two.
  const matchedKinnserPatientKey = (
    selectedIntake as { kinnser_matched_patient_key?: string | null }
  )?.kinnser_matched_patient_key?.trim();

  const { schedulingInKinnser, schedulingResult, handleScheduleInKinnser } =
    useKinnserScheduling({
      intakeId: selectedIntake?.id,
      intakeStatus: selectedIntake?.status,
      taskName: schedulingTaskName,
      tabName: schedulingTabName,
      taskDate: schedulingTaskDate,
      clinicianId: selectedKinnserClinicianId,
    });

  // Roger: scheduling on an existing matched patient (SOC with new admission/episode, or ROC).
  // Unlike Kinnser which creates a task on an existing Kinnser episode, Roger needs to create
  // Admission/Episode/Visit rows in our own DB.
  const { schedulingInRoger, rogerSchedulingResult, handleScheduleInRoger } =
    useRogerScheduling({
      intakeId: selectedIntake?.id,
      intakeStatus: selectedIntake?.status,
    });

  const { autoAddInKinnser, autoAddResult, handleAutoAddInKinnser } =
    useKinnserAutoAddFlow({
      intakeId: selectedIntake?.id,
      intakeStatus: selectedIntake?.status,
      getValues: () => {
        const v = getValues() as unknown as {
          kinnser_soc_date?: string | null;
          kinnser_episode_start_date?: string | null;
          kinnser_clinician_id?: string | null;
        };
        const formClinician =
          v.kinnser_clinician_id &&
          typeof v.kinnser_clinician_id === 'string' &&
          v.kinnser_clinician_id.trim() !== ''
            ? v.kinnser_clinician_id.trim()
            : null;
        const intakeClinician =
          selectedIntake?.kinnser_clinician_id &&
          typeof selectedIntake.kinnser_clinician_id === 'string' &&
          selectedIntake.kinnser_clinician_id.trim() !== ''
            ? selectedIntake.kinnser_clinician_id.trim()
            : null;
        return {
          kinnser_soc_date: v.kinnser_soc_date ?? null,
          kinnser_episode_start_date: v.kinnser_episode_start_date ?? null,
          kinnser_clinician_id: formClinician ?? intakeClinician ?? null,
        };
      },
    });

  const shouldFetchKinnserClinicData =
    hasKinnserOption &&
    (pushToKinnser || selectedIntake?.push_to_kinnser === true);
  const { clinicData: schedulingClinicData } = useSchedulingClinicData({
    agencySystemName: user?.agency?.system_name,
    kinnserClinicId: selectedIntake?.kinnser_clinic_id,
    enabled: shouldFetchKinnserClinicData,
  });

  const isWrmClinic = isClinicWrmEnabled(
    agencyClinics,
    selectedIntake?.agency_clinic_id
  );
  const kinnserWrmPushUx = Boolean(isWrmClinic && pushToKinnser);

  const schedulingClinicContextForValidation = useMemo(() => {
    if (!schedulingClinicData) return null;
    return {
      ...schedulingClinicData,
      use_kinnser_wrm: isWrmClinic,
    };
  }, [schedulingClinicData, isWrmClinic]);

  // Skip first run so we don't overwrite live validation left by other tabs (e.g. Scheduler) when switching to Push to EMR.
  // Re-run only when intake/schema changes (e.g. after save from another tab) so we have up-to-date state.
  const isFirstValidationRunRef = useRef(true);
  useEffect(() => {
    if (isFirstValidationRunRef.current) {
      isFirstValidationRunRef.current = false;
      return;
    }
    computeAndSetIntakeTabValidation(
      selectedIntake ?? null,
      intakeFormSchema ?? null,
      setIntakeTabValid
    );
  }, [selectedIntake, intakeFormSchema, setIntakeTabValid]);

  // Track if we've already initialized EMR selection for this intake
  const emrSelectionInitialized = useRef<string | null>(null);
  const [initializingEmrSelection, setInitializingEmrSelection] =
    useState(false);
  // Prevents duplicate confirmTabStatus calls when this effect re-runs before
  // onStatusChange() has refetched and updated tab_statuses to 'complete'.
  const hasAutoConfirmedPushToEmrRef = useRef(false);
  const rogerCreateResponse = pushToRoger
    ? (rogerCreateResult?.response ?? null)
    : null;

  // Reset auto-confirm tracking when switching to a different intake
  useEffect(() => {
    hasAutoConfirmedPushToEmrRef.current = false;
  }, [selectedIntake?.id]);

  // Auto-mark Push to EMR tab complete when onboarding/create succeeds (or ROC/SOC scheduling or auto-add completed)
  useEffect(() => {
    const isAutoAddSuccess =
      isSocNewAdmissionOrEpisodeModeForFlow && autoAddResult?.success === true;
    const isKinnserScheduledSuccess =
      (isRocMode || isSocExistingPatientMode) &&
      pushToKinnser &&
      schedulingResult?.success === true;
    const isRogerScheduledSuccess =
      (isRocModeRoger || isSocExistingPatientModeRoger) &&
      pushToRoger &&
      rogerSchedulingResult?.success === true;
    const hasResult =
      createResult ||
      isKinnserScheduledSuccess ||
      isRogerScheduledSuccess ||
      isAutoAddSuccess;
    if (
      !selectedIntake?.id ||
      !hasResult ||
      hasAutoConfirmedPushToEmrRef.current
    )
      return;

    const isRogerOnboardSuccess =
      pushToRoger &&
      !isRocModeRoger &&
      !isSocExistingPatientModeRoger &&
      rogerCreateResponse?.success === true;
    const isKinnserSuccess =
      pushToKinnser &&
      !isRocMode &&
      !isSocExistingPatientMode &&
      (isSocNewAdmissionOrEpisodeModeForFlow
        ? autoAddResult?.success === true
        : isUsingOnboarding
          ? (createResult as KinnserOnboardPatientResponse)?.patient_creation
              ?.success === true
          : (createResult as KinnserCreatePatientResponse)?.success === true);
    const isSuccess =
      isRogerOnboardSuccess ||
      isKinnserSuccess ||
      isKinnserScheduledSuccess ||
      isRogerScheduledSuccess ||
      isAutoAddSuccess;
    const tabComplete =
      selectedIntake.tab_statuses?.push_to_emr === 'complete' ||
      selectedIntake.tab_statuses?.push_to_emr === 'complete_with_errors';
    if (!isSuccess || tabComplete) return;

    hasAutoConfirmedPushToEmrRef.current = true;
    confirmTabStatus(selectedIntake.id, 'push_to_emr', 'complete')
      .then(() => {
        onStatusChange?.();
      })
      .catch(() => {
        showError(
          'Failed to mark tab complete',
          'The Push to EMR tab could not be marked complete.'
        );
      });
  }, [
    selectedIntake?.id,
    selectedIntake?.tab_statuses?.push_to_emr,
    createResult,
    rogerCreateResponse?.success,
    schedulingResult,
    rogerSchedulingResult,
    autoAddResult,
    isRocMode,
    isRocModeRoger,
    isSocExistingPatientMode,
    isSocExistingPatientModeRoger,
    isSocNewAdmissionOrEpisodeModeForFlow,
    pushToRoger,
    pushToKinnser,
    isUsingOnboarding,
    confirmTabStatus,
    onStatusChange,
    showError,
  ]);

  // Initialize EMR selection when entering the Push to EMR step
  // This auto-selects the EMR option if only one is enabled
  useEffect(() => {
    const initializeEmrSelection = async () => {
      if (!selectedIntake?.id) return;

      // Only initialize once per intake
      if (emrSelectionInitialized.current === selectedIntake.id) return;
      emrSelectionInitialized.current = selectedIntake.id;

      setInitializingEmrSelection(true);
      try {
        const response =
          await initializeEmrSelectionEndpointIntakeIntakeIdInitializeEmrSelectionPost(
            {
              path: { intake_id: selectedIntake.id },
            }
          );

        // Update form values if the backend auto-set the flags
        if (response.data) {
          if (
            response.data.push_to_roger !== selectedIntake.push_to_roger ||
            response.data.push_to_kinnser !== selectedIntake.push_to_kinnser
          ) {
            setValue('push_to_roger', response.data.push_to_roger ?? false);
            setValue('push_to_kinnser', response.data.push_to_kinnser ?? false);
          }
        }
      } catch (error) {
        // Silently fail if the API call fails
        console.error('Failed to initialize EMR selection:', error);
      } finally {
        setInitializingEmrSelection(false);
      }
    };

    initializeEmrSelection();
  }, [
    selectedIntake?.id,
    selectedIntake?.push_to_roger,
    selectedIntake?.push_to_kinnser,
    setValue,
  ]);

  if (!selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
      </div>
    );
  }

  const pushDisabledByIntakeState = !!(
    selectedIntake?.archived || selectedIntake?.non_admit
  );

  const persistedPushFailed = selectedIntake?.status === 'push_failed';
  const pushActionBlocked = persistedPushFailed;

  // Only Scheduling and Push to EMR tab block onboarding; other tabs (Extraction, Eligibility, etc.) do not.
  const TAB_LABELS: Record<IntakeTabValidationKey, string> = {
    [IntakeTabValidationKey.Extraction]: 'Extraction',
    [IntakeTabValidationKey.Eligibility]: 'Eligibility',
    [IntakeTabValidationKey.PriorAuth]: 'Prior Authorization',
    [IntakeTabValidationKey.ReadyToCall]: 'Ready to Call',
    [IntakeTabValidationKey.Scheduling]: 'Scheduling',
  };
  const tabsRequiredForPushToEmrGate: IntakeTabValidationKey[] = [
    // When visit type is ROC or SOC existing patient, Scheduling validations do not block the Push to EMR step.
    ...(!isRocMode && !isSocExistingPatientMode
      ? [IntakeTabValidationKey.Scheduling]
      : []),
  ];

  // Scheduling tab uses clinic-aware validation (e.g. secondary scheduler required). On-load validation
  // uses the base schema without clinic context, so we re-check with clinic data so Push to EMR gate matches the tab.
  const schedulingValidFromClinicData = useMemo(
    () =>
      selectedIntake && schedulingClinicContextForValidation != null
        ? validateSchedulingFromIntakeWithClinicData(
            selectedIntake,
            schedulingClinicContextForValidation
          )
        : true,
    [selectedIntake, schedulingClinicContextForValidation]
  );

  const shouldCallScheduleInKinnser =
    !isSocNewAdmissionOrEpisodeModeForFlow &&
    (isRocMode || isSocExistingPatientMode) &&
    pushToKinnser;
  // This boolean is checking whether the “Kinnser task clinician” requirement has been satisfied.
  const hasRequiredKinnserTaskClinician =
    !shouldCallScheduleInKinnser ||
    isWrmClinic ||
    selectedKinnserClinicianId != null;
  const kinnserTaskScheduleBlockedByWrm =
    shouldCallScheduleInKinnser && isWrmClinic;

  const isTabValidForGate = (tab: IntakeTabValidationKey): boolean => {
    if (tab === IntakeTabValidationKey.Scheduling) {
      return (
        intakeTabValidation[tab] === true &&
        schedulingValidFromClinicData &&
        !awaitingSchedulingSelection
      );
    }
    return intakeTabValidation[tab] === true;
  };

  const priorTabsValid = tabsRequiredForPushToEmrGate.every(isTabValidForGate);
  const incompleteStepNames = tabsRequiredForPushToEmrGate
    .filter(tab => !isTabValidForGate(tab))
    .map(tab => TAB_LABELS[tab] ?? tab);

  const isLoading =
    loadingClinics || loadingClinicData || initializingEmrSelection;

  // Existing patient is confirmed when the intake has a persisted matched patient:
  //   - Kinnser: kinnser_matched_patient_key (Extraction or Push-to-EMR match modal)
  //   - Roger:   intake.patient_id linked to an existing Roger patient
  const hasMatchedRogerPatient =
    isExistingPatient && !!selectedIntake?.patient_id;
  const rocHasSelectedPatient =
    !!matchedKinnserPatientKey || hasMatchedRogerPatient;
  const rocVisitTypeLabel =
    selectedIntake?.kinnser_visit_type === 'roc'
      ? 'ROC'
      : (selectedIntake?.kinnser_visit_type ?? 'visit');
  const allTabsValid =
    isRocMode || isSocExistingPatientMode
      ? priorTabsValid &&
        rocHasSelectedPatient &&
        hasRequiredKinnserTaskClinician
      : isValid && priorTabsValid;

  // Scheduling flows (matched patient, no new-patient creation):
  //   - Kinnser ROC / SOC-existing: task on existing Kinnser episode (handleScheduleInKinnser)
  //   - Roger   ROC / SOC-existing: create Admission/Episode/Visit on existing Roger patient
  //     (handleScheduleInRoger)
  // SOC new-admission/episode on existing Kinnser patient uses the auto-add flow; new patients
  // always go through standard onboarding/creation.
  const visitScheduleHandler = isSocNewAdmissionOrEpisodeModeForFlow
    ? handleAutoAddInKinnser
    : shouldCallScheduleInKinnser && !isWrmClinic
      ? handleScheduleInKinnser
      : (isRocModeRoger || isSocExistingPatientModeRoger) && pushToRoger
        ? handleScheduleInRoger
        : undefined;

  // awaiting_scheduling is never a push state: wire a no-op (like the WRM
  // block) so an existing patient can never fall through to new-patient
  // creation even if the disable gate regresses.
  const kinnserPushClickHandler =
    visitScheduleHandler ??
    (kinnserTaskScheduleBlockedByWrm || awaitingSchedulingSelection
      ? async () => {}
      : handleCreateInKinnser);

  const rogerPushClickHandler =
    visitScheduleHandler ??
    (awaitingSchedulingSelection ? async () => {} : handleCreateInRoger);

  const pushCreateActionDisabled =
    (!pushToRoger && !pushToKinnser) ||
    (isNewPatient && !shouldCreateEpisode) ||
    Boolean(pushToKinnser && kinnserTaskScheduleBlockedByWrm) ||
    socKinnserBlockedByOpenAdmission;

  const creatingForButton = isSocNewAdmissionOrEpisodeModeForFlow
    ? creatingInEMR || autoAddInKinnser
    : shouldCallScheduleInKinnser && !isWrmClinic
      ? creatingInEMR || schedulingInKinnser
      : (isRocModeRoger || isSocExistingPatientModeRoger) && pushToRoger
        ? creatingInEMR || schedulingInRoger
        : creatingInEMR;
  const visibleCreateResult: DisplayCreateResult = pushToRoger
    ? rogerCreateResponse
    : (createResult as
        | KinnserCreatePatientResponse
        | KinnserOnboardPatientResponse
        | null);
  const defaultAutoAddTaskName =
    creationType === 'new_admission'
      ? 'OASIS-E2 Start of Care (new admission)'
      : 'OASIS-E2 Start of Care (new episode)';
  const autoAddTaskKey =
    getStringField(autoAddResult, 'upload_patient_task_key') ??
    getStringField(autoAddResult, 'soc_patient_task_key');
  const autoAddTaskName =
    getStringField(autoAddResult, 'upload_task_name') ??
    getStringField(autoAddResult, 'soc_task_name') ??
    defaultAutoAddTaskName;

  const displayCreateResult =
    isSocNewAdmissionOrEpisodeModeForFlow && autoAddResult != null
      ? ({
          success: autoAddResult.success,
          message: autoAddResult.message,
          ...(autoAddTaskKey ? { patient_task_key: autoAddTaskKey } : {}),
          task_name: autoAddTaskName,
        } as KinnserCreatePatientResponse)
      : shouldCallScheduleInKinnser && !isWrmClinic && schedulingResult != null
        ? ({
            success: schedulingResult.success,
            message: schedulingResult.message,
            ...('patient_task_key' in schedulingResult &&
            schedulingResult.patient_task_key
              ? {
                  patient_task_key: schedulingResult.patient_task_key,
                }
              : {}),
            ...('task_name' in schedulingResult && schedulingResult.task_name
              ? { task_name: schedulingResult.task_name }
              : {}),
          } as KinnserCreatePatientResponse)
        : (isRocModeRoger || isSocExistingPatientModeRoger) &&
            pushToRoger &&
            rogerSchedulingResult != null
          ? rogerSchedulingResult
          : visibleCreateResult;

  const showKinnserPushCompletedCopy =
    pushToKinnser &&
    (selectedIntake.status === 'pushed_to_kinnser' ||
      (isUsingOnboarding
        ? (displayCreateResult as KinnserOnboardPatientResponse | null)
            ?.patient_creation?.success === true
        : (displayCreateResult as KinnserCreatePatientResponse | null)
            ?.success === true) ||
      (isSocExistingPatientMode &&
        (displayCreateResult as { success?: boolean } | null)?.success ===
          true));

  const showRogerPushCompletedCopy =
    pushToRoger &&
    (selectedIntake.status === 'pushed_to_roger' ||
      (displayCreateResult as RogerOnboardPatientResponse | null)?.success ===
        true);

  let rocSocExistingMatchedPatientBodyCopy: string;
  if (kinnserWrmPushUx && !pushToRoger) {
    rocSocExistingMatchedPatientBodyCopy = showKinnserPushCompletedCopy
      ? 'For WRM, you will need to manually schedule the visit in Kinnser.'
      : "The patient has been matched. Use Push to Kinnser when you are ready to send the patient's data.";
  } else if (pushToRoger) {
    rocSocExistingMatchedPatientBodyCopy = showRogerPushCompletedCopy
      ? ''
      : 'The patient has been matched. Continue to schedule the visit in Roger.';
  } else {
    rocSocExistingMatchedPatientBodyCopy = showKinnserPushCompletedCopy
      ? ''
      : 'The patient has been matched. Continue to schedule the visit in Kinnser.';
  }

  return (
    <>
      <div className='h-full flex flex-col'>
        <Card className='h-full flex flex-col'>
          <CardHeader className='border-b py-3 px-4'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                {saveStatus === 'saving' && (
                  <div className='flex items-center gap-2 text-xs text-gray-500'>
                    <Loader2 className='h-3 w-3 animate-spin' />
                    <span>Saving...</span>
                  </div>
                )}
                {saveStatus === 'saved' && (
                  <div className='text-xs text-green-600'>Saved</div>
                )}
                {saveStatus === 'error' && (
                  <div className='text-xs text-red-600'>Error saving</div>
                )}
                {persistedPushFailed && (
                  <Alert className='py-2 px-3 bg-red-50 border-red-200'>
                    <AlertDescription className='text-red-800 text-xs flex gap-2 items-center'>
                      <AlertTriangle className='h-4 w-4 flex-shrink-0' />
                      <span>Push to EMR failed for this intake.</span>
                    </AlertDescription>
                  </Alert>
                )}
                {kinnserTaskScheduleBlockedByWrm && (
                  <Alert className='py-2 px-3 bg-amber-50 border-amber-200'>
                    <AlertDescription className='text-amber-900 text-xs flex gap-2 items-center'>
                      <AlertTriangle className='h-4 w-4 flex-shrink-0' />
                      <span>
                        This clinic uses Wellsky Resource Manager. Pushing is
                        not available for ROC.{' '}
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
                {socKinnserBlockedByOpenAdmission && (
                  <Alert className='py-2 px-3 bg-amber-50 border-amber-200'>
                    <AlertDescription className='text-amber-900 text-xs flex gap-2 items-center'>
                      <AlertTriangle className='h-4 w-4 flex-shrink-0' />
                      <span>
                        A new admission cannot be created while the latest
                        admission is open. See the Scheduling tab for more
                        information.
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              <div className='ml-4 flex flex-row flex-nowrap items-center gap-2 shrink-0'>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className='inline-flex shrink-0'>
                        <PatientCreateButton
                          handleCreate={
                            pushToRoger
                              ? rogerPushClickHandler
                              : pushToKinnser
                                ? kinnserPushClickHandler
                                : async () => {}
                          }
                          onComplete={onComplete}
                          creating={creatingForButton}
                          saveStatus={saveStatus}
                          loadingClinics={loadingClinics}
                          loadingClinicData={loadingClinicData}
                          selectedIntake={selectedIntake}
                          isUsingOnboarding={isUsingOnboarding}
                          preferOnboardLabel={Boolean(
                            (isSocNewAdmissionOrEpisodeMode || isNewPatient) &&
                              pushToKinnser
                          )}
                          isExistingPatient={!isNewPatient}
                          pushToEmrTabComplete={
                            selectedIntake.tab_statuses?.push_to_emr ===
                              'complete' ||
                            selectedIntake.tab_statuses?.push_to_emr ===
                              'complete_with_errors'
                          }
                          createResult={displayCreateResult}
                          isValid={allTabsValid}
                          useKinnserWrmPushCopy={kinnserWrmPushUx}
                          pushToRoger={pushToRoger}
                          pushToKinnser={pushToKinnser}
                          pushDisabledByIntakeState={pushDisabledByIntakeState}
                          pushActionDisabled={pushCreateActionDisabled}
                          disableAfterFailure={pushActionBlocked}
                          mode={
                            pushMode.kind === 'roc' ||
                            pushMode.kind === 'rec' ||
                            pushMode.kind === 'soc_existing' ||
                            pushMode.kind === 'awaiting_scheduling'
                              ? pushMode.kind
                              : 'create'
                          }
                          hasSelectedPatient={rocHasSelectedPatient}
                          className='shrink-0'
                        />
                      </span>
                    </TooltipTrigger>
                    {pushDisabledByIntakeState && (
                      <TooltipContent
                        side='bottom'
                        className='max-w-xs text-xs'
                      >
                        Push to EMR is not available for archived or non-admit
                        intakes.
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                {selectedIntake && (
                  <MarkCompletedButton
                    intakeId={selectedIntake.id}
                    tabKey='push_to_emr'
                    isComplete={
                      selectedIntake.tab_statuses?.push_to_emr === 'complete' ||
                      selectedIntake.tab_statuses?.push_to_emr ===
                        'complete_with_errors'
                    }
                    onStatusChange={onStatusChange}
                    disabled={creatingForButton || pushDisabledByIntakeState}
                    completeButtonLabel={
                      isRocMode ||
                      isSocExistingPatientMode ||
                      isSocNewAdmissionOrEpisodeMode ||
                      isNewPatient
                        ? 'Mark Complete'
                        : 'Mark Complete without Onboarding'
                    }
                    className='shrink-0'
                  />
                )}
              </div>
            </div>

            <div className='mt-3'>
              <TooltipProvider>
                <SchedulingEmrSelectionHeader
                  control={control}
                  setValue={(name, value) =>
                    setValue(
                      name as keyof CreateEpisodeStepFormData,
                      value as CreateEpisodeStepFormData[keyof CreateEpisodeStepFormData]
                    )
                  }
                  saveToServer={saveToServer}
                  showRogerSchedulingOption={showRogerSchedulingOption}
                  showKinnserSchedulingOption={showKinnserSchedulingOption}
                  schedulingSelectionLocked={schedulingSelectionLocked}
                  pushCompleted={
                    showRogerPushCompletedCopy ||
                    showKinnserPushCompletedCopy ||
                    isSchedulingPushCompleted(selectedIntake.status)
                  }
                  scheduleRogerBlockedByMatch={scheduleRogerBlockedByMatch}
                  scheduleKinnserBlockedByMatch={scheduleKinnserBlockedByMatch}
                  isWrmClinic={isWrmClinic}
                  isNewPatient={isNewPatientRaw}
                  visitType={visitType ?? null}
                  recertDate={selectedIntake.recert_date ?? null}
                />
              </TooltipProvider>
            </div>

            {/* REC (recert onboarding): summary of what pushing will create. */}
            {pushMode.kind === 'rec' &&
              getRecDerivedDates(
                selectedIntake.kinnser_episode_start_date ??
                  selectedIntake.episode_start_date
              ) && (
                <Alert className='mt-3 py-2 px-3 bg-blue-50 border-blue-200'>
                  <AlertDescription className='text-blue-900 text-xs'>
                    <div className='font-medium mb-1'>
                      Pushing will create in Roger:
                    </div>
                    <div className='space-y-0.5'>
                      <RecOnboardingSummaryLines
                        episodeStartDate={
                          selectedIntake.kinnser_episode_start_date ??
                          selectedIntake.episode_start_date
                        }
                        admissionDate={selectedIntake.soc_date}
                        recertDate={selectedIntake.recert_date}
                      />
                    </div>
                  </AlertDescription>
                </Alert>
              )}

            {/* Red validation warning: tab(s) to complete (e.g. Scheduling for new patients) and required fields on this tab.
                In ROC or SOC existing patient mode, Scheduling does not block so we skip; for new patients we show this so they fix Scheduling first.
                Existing patients never use the create form, so its required-field errors are hidden for them (their push is gated on Scheduling instead).
                After a push failure, hide required-field hints—they are misleading while the action is blocked. */}
            {!isLoading &&
              !isRocMode &&
              !isSocExistingPatientMode &&
              (incompleteStepNames.length > 0 ||
                (Object.keys(errors).length > 0 &&
                  !pushActionBlocked &&
                  !isExistingPatient)) && (
                <Alert className='mt-3 py-2 px-3 bg-red-50 border-red-200 text-red-900 text-xs'>
                  <div className='flex items-start gap-2'>
                    <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-red-600' />
                    <div className='flex-1 space-y-1 text-red-800'>
                      {incompleteStepNames.length > 0 && (
                        <IntakeValidationErrorList
                          title='Complete these steps before pushing to EMR:'
                          ariaLabel='Tabs to complete'
                          items={tabsRequiredForPushToEmrGate
                            .filter(tab => !isTabValidForGate(tab))
                            .map(tab => {
                              const label = TAB_LABELS[tab] ?? tab;
                              const tabId = VALIDATION_KEY_TO_INTAKE_TAB[tab];
                              const item: IntakeValidationErrorItem = {
                                id: tab,
                                label,
                                onClick: onNavigateToTab
                                  ? () => onNavigateToTab(tabId)
                                  : undefined,
                              };
                              return item;
                            })}
                        />
                      )}
                      {Object.keys(errors).length > 0 &&
                        !pushActionBlocked &&
                        !isExistingPatient && (
                          <IntakeValidationErrorList
                            title='Required fields on this tab:'
                            ariaLabel='Required fields'
                            items={Object.entries(errors).map(
                              ([fieldName, error]) => {
                                const message =
                                  (error as { message?: string })?.message ??
                                  '';
                                const cleanedMessage = message.replace(
                                  / is required$/,
                                  ''
                                );
                                const label =
                                  cleanedMessage || getFieldLabel(fieldName);
                                return {
                                  id: fieldName,
                                  label,
                                  onClick: resolveErrorFieldAction(fieldName, {
                                    onNavigateToTab,
                                    onScrollToField,
                                  }),
                                };
                              }
                            )}
                          />
                        )}
                    </div>
                  </div>
                </Alert>
              )}
            {/* Validation errors or success message */}
            {(visibleCreateResult ||
              (isSocNewAdmissionOrEpisodeModeForFlow && autoAddResult) ||
              (shouldCallScheduleInKinnser &&
                !isWrmClinic &&
                schedulingResult) ||
              ((isRocModeRoger || isSocExistingPatientModeRoger) &&
                pushToRoger &&
                rogerSchedulingResult)) && (
              <div className='mt-3'>
                {isSocNewAdmissionOrEpisodeModeForFlow && autoAddResult ? (
                  autoAddResult.success ? (
                    <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                      <div className='flex items-center gap-2 text-green-800'>
                        <CheckCircle className='h-4 w-4' />
                        <span className='font-medium'>
                          {creationType === 'new_admission'
                            ? 'New admission and episode created in Kinnser'
                            : 'New episode created in Kinnser'}
                        </span>
                      </div>
                      <div className='text-sm text-green-700 mt-2'>
                        {autoAddResult.message}
                      </div>
                      {autoAddTaskKey && (
                        <div className='text-sm text-green-700 mt-1'>
                          Task: {autoAddTaskName} ({autoAddTaskKey})
                        </div>
                      )}
                    </div>
                  ) : (
                    (() => {
                      const genericAutoAddFailure =
                        typeof autoAddResult.message === 'string' &&
                        autoAddResult.message.trim() ===
                          'Auto-add failed. Please try again.';
                      // Never suppress a surfaceable business error.
                      if (
                        persistedPushFailed &&
                        genericAutoAddFailure &&
                        !autoAddResult.surfaceable
                      ) {
                        return null;
                      }
                      return (
                        <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                          <div className='flex items-center gap-2 text-red-800 mb-2'>
                            <XCircle className='h-4 w-4' />
                            <span className='font-medium'>
                              {creationType === 'new_admission'
                                ? 'New admission and episode'
                                : 'New episode'}{' '}
                              incomplete
                            </span>
                          </div>
                          <div className='text-sm text-red-700'>
                            {autoAddResult.message}
                          </div>
                        </div>
                      );
                    })()
                  )
                ) : shouldCallScheduleInKinnser &&
                  !isWrmClinic &&
                  schedulingResult ? (
                  schedulingResult.success ? (
                    <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                      <div className='flex items-center gap-2 text-green-800'>
                        <CheckCircle className='h-4 w-4' />
                        <span className='font-medium'>
                          {isSocExistingPatientMode ? 'SOC' : 'ROC'} scheduled
                          on existing episode in Kinnser
                        </span>
                      </div>
                      <div className='text-sm text-green-700 mt-2'>
                        {schedulingResult.message}
                      </div>
                      {'patient_task_key' in schedulingResult &&
                        schedulingResult.patient_task_key && (
                          <div className='text-sm text-green-700 mt-1'>
                            Task:{' '}
                            {('task_name' in schedulingResult &&
                              schedulingResult.task_name) ??
                              schedulingTaskName}{' '}
                            ({schedulingResult.patient_task_key})
                          </div>
                        )}
                    </div>
                  ) : (
                    (() => {
                      const genericSchedulingFailure =
                        typeof schedulingResult.message === 'string' &&
                        schedulingResult.message.trim() ===
                          'Scheduling failed. Please try again.';
                      // Never suppress a surfaceable business error.
                      if (
                        persistedPushFailed &&
                        genericSchedulingFailure &&
                        !schedulingResult.surfaceable
                      ) {
                        return null;
                      }
                      return (
                        <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                          <div className='flex items-center gap-2 text-red-800 mb-2'>
                            <XCircle className='h-4 w-4' />
                            <span className='font-medium'>
                              {isSocExistingPatientMode ? 'SOC' : 'ROC'}{' '}
                              scheduling incomplete
                            </span>
                          </div>
                          <div className='text-sm text-red-700'>
                            {schedulingResult.message}
                          </div>
                        </div>
                      );
                    })()
                  )
                ) : pushToRoger ? (
                  (() => {
                    const isRogerSchedulingMode =
                      isRocModeRoger || isSocExistingPatientModeRoger;
                    const rogerResult = (
                      isRogerSchedulingMode
                        ? rogerSchedulingResult
                        : visibleCreateResult
                    ) as RogerOnboardResultWithSurfaceable | null;
                    if (!rogerResult) return null;
                    const successHeader = isRogerSchedulingMode
                      ? `${isRocModeRoger ? 'ROC' : 'SOC'} scheduled on existing Roger patient`
                      : isUsingOnboarding
                        ? 'Patient, episode, task, and attachment created in Roger'
                        : 'Patient created successfully in Roger';
                    const failureHeader = isRogerSchedulingMode
                      ? `${isRocModeRoger ? 'ROC' : 'SOC'} scheduling incomplete`
                      : isUsingOnboarding
                        ? 'Onboarding Failed'
                        : 'Validation Errors';
                    return rogerResult.success ? (
                      <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                        <div className='flex items-center justify-between text-green-800'>
                          <div className='flex items-center gap-2'>
                            <CheckCircle className='h-4 w-4' />
                            <span className='font-medium'>{successHeader}</span>
                          </div>
                          {rogerResult.patient_id && (
                            <a
                              href={`/patients/${rogerResult.patient_id}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='flex items-center gap-1 text-sm font-medium text-green-700 hover:text-green-900 underline'
                            >
                              View Patient
                              <ExternalLink className='h-3 w-3' />
                            </a>
                          )}
                        </div>
                        {rogerResult.message && (
                          <div className='text-sm text-green-700 mt-2'>
                            {rogerResult.message}
                          </div>
                        )}
                      </div>
                    ) : (
                      (() => {
                        const genericOnboardFailure =
                          typeof rogerResult.message === 'string' &&
                          rogerResult.message.trim() ===
                            'Failed to onboard patient. Please try again.';
                        // Never suppress a surfaceable business error.
                        if (
                          persistedPushFailed &&
                          isUsingOnboarding &&
                          genericOnboardFailure &&
                          !rogerResult.surfaceable
                        ) {
                          return null;
                        }
                        return (
                          <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                            {isUsingOnboarding &&
                            !isRogerSchedulingMode ? null : (
                              <div className='flex items-center gap-2 text-red-800 mb-2'>
                                <XCircle className='h-4 w-4' />
                                <span className='font-medium'>
                                  {failureHeader}
                                </span>
                              </div>
                            )}
                            <div className='text-sm text-red-700'>
                              {rogerResult.message}
                            </div>
                          </div>
                        );
                      })()
                    );
                  })()
                ) : isUsingOnboarding ? (
                  // Onboarding result display
                  (createResult as KinnserOnboardPatientResponse)
                    .patient_creation.success ? (
                    <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                      <div className='flex items-center gap-2 text-green-800'>
                        <CheckCircle className='h-4 w-4' />
                        <span className='font-medium'>
                          {(createResult as KinnserOnboardPatientResponse)
                            .task_creation.patient_task_key
                            ? 'Patient, admission, episode, task, and attachment created in Kinnser'
                            : 'Patient, admission, and episode created in Kinnser'}
                        </span>
                      </div>
                      <div className='text-sm text-green-700 mt-2 space-y-1'>
                        {(createResult as KinnserOnboardPatientResponse)
                          .task_creation.patient_task_key && (
                          <div>
                            Task:{' '}
                            {
                              (createResult as KinnserOnboardPatientResponse)
                                .task_creation.task_name
                            }{' '}
                            (
                            {
                              (createResult as KinnserOnboardPatientResponse)
                                .task_creation.patient_task_key
                            }
                            )
                          </div>
                        )}
                        {(createResult as KinnserOnboardPatientResponse).upload
                          .attachments?.length > 0 && (
                          <div>
                            Uploaded:{' '}
                            {(
                              createResult as KinnserOnboardPatientResponse
                            ).upload.attachments
                              .map(
                                att =>
                                  att.client_file ||
                                  att.server_file ||
                                  'Unknown'
                              )
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    (() => {
                      const onboardResp =
                        createResult as KinnserOnboardPatientResponse;
                      const msg = onboardResp.patient_creation.message;
                      const genericOnboardFailure =
                        typeof msg === 'string' &&
                        msg.trim() ===
                          'Failed to onboard patient. Please try again.';
                      // Never suppress a surfaceable business error.
                      if (
                        persistedPushFailed &&
                        genericOnboardFailure &&
                        !onboardResp.surfaceable
                      ) {
                        return null;
                      }
                      const validationErrors: KinnserOnboardValidationError[] =
                        onboardResp.validation_errors ?? [];
                      return (
                        <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                          <div className='flex items-center gap-2 text-red-800 mb-2'>
                            <XCircle className='h-4 w-4' />
                            <span className='font-medium'>
                              {validationErrors.length > 0
                                ? 'Fix these issues before pushing to EMR'
                                : 'Onboarding failed'}
                            </span>
                          </div>
                          {validationErrors.length > 0 ? (
                            <IntakeValidationErrorList
                              title=''
                              ariaLabel='Validation errors'
                              className='text-sm text-red-700'
                              items={validationErrors.map((err, index) => {
                                const fieldLabel = getFieldLabel(err.field);
                                return {
                                  id: `${err.field}-${index}`,
                                  label: fieldLabel,
                                  detail: err.message,
                                  onClick: onScrollToField
                                    ? () => onScrollToField(err.field)
                                    : undefined,
                                };
                              })}
                            />
                          ) : (
                            <div className='text-sm text-red-700'>{msg}</div>
                          )}
                        </div>
                      );
                    })()
                  )
                ) : // Creation result display
                (createResult as KinnserCreatePatientResponse).success ? (
                  <div className='p-3 bg-green-50 border border-green-200 rounded-md'>
                    <div className='flex items-center gap-2 text-green-800'>
                      <CheckCircle className='h-4 w-4' />
                      <span className='font-medium'>
                        Patient created successfully in Kinnser
                      </span>
                    </div>
                    {(createResult as KinnserCreatePatientResponse)
                      .kinnser_patient_id && (
                      <div className='text-sm text-green-700 mt-1'>
                        Kinnser Patient ID:{' '}
                        {
                          (createResult as KinnserCreatePatientResponse)
                            .kinnser_patient_id
                        }
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='p-3 bg-red-50 border border-red-200 rounded-md'>
                    <div className='flex items-center gap-2 text-red-800 mb-2'>
                      <XCircle className='h-4 w-4' />
                      <span className='font-medium'>Validation Errors</span>
                    </div>
                    <ul className='list-disc list-inside text-sm text-red-700 space-y-1'>
                      {(
                        createResult as KinnserCreatePatientResponse
                      ).errors?.map((error, index) => (
                        <li key={index}>{error.message}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </CardHeader>
          <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
            {isLoading ? (
              <div className='h-full flex items-center justify-center'>
                <div className='flex flex-col items-center gap-4'>
                  <Loader2 className='h-8 w-8 animate-spin text-gray-400' />
                </div>
              </div>
            ) : (isRocMode || isSocExistingPatientMode) &&
              rocHasSelectedPatient ? (
              <div className='flex flex-col items-center h-full py-12 px-4 text-center'>
                <div className='bg-gray-50 p-4 rounded-full mb-4'>
                  <CalendarDays className='h-8 w-8 text-gray-400' />
                </div>
                <h3 className='text-lg font-medium text-gray-900'>
                  {kinnserWrmPushUx && !pushToRoger
                    ? showKinnserPushCompletedCopy
                      ? 'Pushed to Kinnser'
                      : 'Push to Kinnser'
                    : `Schedule ${
                        isSocExistingPatientMode ? 'SOC' : rocVisitTypeLabel
                      } in ${pushToRoger ? 'Roger' : 'Kinnser'}`}
                </h3>
                <p className='text-sm text-gray-500 mt-1 max-w-sm'>
                  {rocSocExistingMatchedPatientBodyCopy}
                </p>
              </div>
            ) : isRocMode || isSocExistingPatientMode ? (
              <div className='flex flex-col items-center h-full py-12 px-4 text-center'>
                <div className='bg-gray-50 p-4 rounded-full mb-4'>
                  <CalendarDays className='h-8 w-8 text-gray-400' />
                </div>
                <h3 className='text-lg font-medium text-gray-900'>
                  Select patient on Extraction
                </h3>
                <p className='text-sm text-gray-500 mt-1 max-w-sm'>
                  Go to the Extraction step to select an existing patient, then
                  return here to{' '}
                  {kinnserWrmPushUx && !pushToRoger
                    ? 'push the patient to Kinnser.'
                    : `schedule the visit in ${pushToRoger ? 'Roger' : 'Kinnser'}.`}
                </p>
              </div>
            ) : isExistingPatient && (pushToRoger || pushToKinnser) ? (
              <div className='flex flex-col items-center h-full py-12 px-4 text-center'>
                <div className='bg-gray-50 p-4 rounded-full mb-4'>
                  <CalendarDays className='h-8 w-8 text-gray-400' />
                </div>
                <h3 className='text-lg font-medium text-gray-900'>
                  {kinnserWrmPushUx
                    ? 'Finish on Scheduling tab'
                    : 'Schedule on Scheduling tab'}
                </h3>
                <p className='text-sm text-gray-500 mt-1 max-w-sm'>
                  {kinnserWrmPushUx
                    ? 'This patient is matched as existing. Use the Scheduling tab to enter push details; no fields are required on this tab.'
                    : 'This patient is matched as existing. Use the Scheduling tab to schedule the visit; no fields are required on this tab.'}
                </p>
              </div>
            ) : pushToRoger ? (
              <RogerCreateEpisodeStepFormContent
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                displayValues={{
                  hhCahpsOptOut: hhCahpsOptOut || false,
                }}
                clinics={clinics}
                clinicData={clinicData}
                loadingClinics={loadingClinics}
                loadingClinicData={loadingClinicData}
                insuranceOptions={insuranceOptions}
                selectedIntake={selectedIntake}
                isValidatingAddress={isValidatingAddress}
                handleAddressFieldBlur={handleAddressFieldBlur}
                kinnserLanguageKey={kinnserLanguageKey || undefined}
                rogerPhysicianOptions={rogerPhysicianOptions}
                loadingPhysicians={loadingPhysicians}
                showAIExplanations={showAIExplanations}
                documentNameDict={documentNameDict}
                requireExplicitMrn={pushMode.kind === 'rec'}
              />
            ) : pushToKinnser ? (
              <KinnserCreateEpisodeStepFormContent
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                handleFieldBlur={handleFieldBlur}
                displayValues={{
                  hhCahpsOptOut: hhCahpsOptOut || false,
                  kinnserLanguageKey: kinnserLanguageKey || undefined,
                }}
                saveStatus={saveStatus}
                saveToServer={saveToServer}
                clinics={clinics}
                clinicData={clinicData}
                loadingClinics={loadingClinics}
                loadingClinicData={loadingClinicData}
                insuranceOptions={insuranceOptions}
                selectedIntake={selectedIntake}
                isValidatingAddress={isValidatingAddress}
                handleAddressFieldBlur={handleAddressFieldBlur}
                kinnserLanguageKey={kinnserLanguageKey}
                isFormValid={isValid}
                addressSuggestion={addressSuggestion}
                addressModalOpen={addressModalOpen}
                setAddressModalOpen={setAddressModalOpen}
                handleAcceptAddressSuggestion={handleAcceptAddressSuggestion}
                handleClinicFieldBlur={handleClinicFieldBlur}
                showAIExplanations={showAIExplanations}
                documentNameDict={documentNameDict}
              />
            ) : (
              <div className='h-full flex justify-center'>
                <div className='text-center max-w-md flex flex-col items-center'>
                  <AlertTriangle className='h-12 w-12 text-yellow-500 mb-4' />
                  <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                    {showRogerSchedulingOption || showKinnserSchedulingOption
                      ? 'Scheduling required'
                      : schedulingEmr
                        ? 'Scheduling unavailable'
                        : 'Select a clinic'}
                  </h3>
                  <p className='text-gray-500 mb-6'>
                    {showRogerSchedulingOption || showKinnserSchedulingOption
                      ? 'Complete scheduling on the Scheduling tab before pushing to EMR.'
                      : schedulingEmr
                        ? 'This clinic’s EMR is not available for scheduling. Check agency EMR settings or patient match, or choose a different clinic.'
                        : 'Choose a clinic on the Extraction tab to continue in the correct EMR.'}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AddressSuggestionModal
        addressModalOpen={addressModalOpen}
        setAddressModalOpen={setAddressModalOpen}
        addressSuggestion={addressSuggestion}
        handleAcceptAddressSuggestion={handleAcceptAddressSuggestion}
      />
    </>
  );
};

export default CreateEpisodeStep;
